import type { ReconFlagRow } from './reconFlags.types';

export type ReconFlagNote = NonNullable<ReconFlagRow['notes']>[number];

/** Newest notes first for display. */
export const sortFlagNotesNewestFirst = (
  notes: readonly ReconFlagNote[] | null | undefined,
): ReconFlagNote[] =>
  [...(notes ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

const newestNoteTimestamp = (
  notes: readonly ReconFlagNote[] | null | undefined,
): number => {
  const newest = sortFlagNotesNewestFirst(notes)[0];
  return newest ? new Date(newest.createdAt).getTime() : 0;
};

/** True when `candidate` is at least as fresh as `baseline` (newest note time, then count). */
export const areFlagNotesAtLeastAsFresh = (
  candidate: readonly ReconFlagNote[] | null | undefined,
  baseline: readonly ReconFlagNote[] | null | undefined,
): boolean => {
  const candidateNewest = newestNoteTimestamp(candidate);
  const baselineNewest = newestNoteTimestamp(baseline);
  if (candidateNewest !== baselineNewest) {
    return candidateNewest > baselineNewest;
  }
  return (candidate?.length ?? 0) >= (baseline?.length ?? 0);
};

const newestFlagNoteText = (
  notes: readonly ReconFlagNote[] | null | undefined,
): string => sortFlagNotesNewestFirst(notes)[0]?.text.trim() ?? '';

/** True when the draft contains a new note to persist. */
export const hasUnsavedFlagNoteDraft = (
  draft: string,
  notes: readonly ReconFlagNote[] | null | undefined,
): boolean => {
  const trimmed = draft.trim();
  return trimmed !== '' && trimmed !== newestFlagNoteText(notes);
};
